import { FC } from 'react';
import { ProblemAndAnswer } from '@gmm/sdk-voodoo/shared';
import ProblemPreview, {
  PreviewProps,
  ProblemFromSWR,
} from '~/lib/problemPreview/problemPreview';
import {
  LockedExamProblemParams,
  useLockedExamTemplateProblem,
} from '~/lib/problemPreview/hooks/useLockedExamTemplateProblem';
import {
  useCurrentProblem,
  useExampleProblem,
  useExamProblem,
} from '~/lib/problemPreview/hooks';
import { useLockedSkillDiscoveryProblem } from '~/work/skillDiscovery/hooks/useSkillDiscoveryLockedSkill';

// Props specific to fetching logic
interface FetcherSpecificProps {
  problemType:
    | 'skillDiscoveryLockedExamProblem'
    | 'lockedExamTemplate'
    | 'current'
    | 'exam'
    | 'helpRequested'
    | 'sample';
  studentInClassId?: string;
  studentInTestId?: string;
  examRestoreId?: string;
  currentType?: string;
  helpRequestedProblemData?: ProblemAndAnswer;
  lockedExamProblemParams?: LockedExamProblemParams;
}

// Combine fetcher-specific props with ProblemPreview props, omitting overlaps
type FullscreenProblemFetcherProps = FetcherSpecificProps &
  Omit<PreviewProps, keyof FetcherSpecificProps>;

// Fetcher components
const LockedExamTemplateFetcher: FC<FullscreenProblemFetcherProps> = ({
  lockedExamProblemParams,
  ...previewProps
}) => {
  const response = useLockedExamTemplateProblem(lockedExamProblemParams!, true);
  return <ProblemPreview {...previewProps} {...response} />;
};

const SkillDiscoveryLockedExamProblem: FC<FullscreenProblemFetcherProps> = ({
  lockedExamProblemParams,
  ...previewProps
}) => {
  const response = useLockedSkillDiscoveryProblem(
    lockedExamProblemParams!.lockedExamIdentifier,
    lockedExamProblemParams!.skillId,
    lockedExamProblemParams!.positionWithinSkill,
  );
  return <ProblemPreview {...previewProps} {...response} />;
};

const CurrentProblemFetcher: FC<FullscreenProblemFetcherProps> = ({
  studentInClassId,
  ...previewProps
}) => {
  const { swrResponse } = useCurrentProblem({
    studentInClassId: studentInClassId!,
  });
  return <ProblemPreview {...previewProps} {...swrResponse} />;
};

const ExamProblemFetcher: FC<FullscreenProblemFetcherProps> = ({
  studentInTestId,
  examRestoreId,
  ...previewProps
}) => {
  const response = useExamProblem({
    problemId: examRestoreId!,
    studentInTestId: studentInTestId!,
  });
  return <ProblemPreview {...previewProps} {...response} />;
};

const HelpRequestedFetcher: FC<FullscreenProblemFetcherProps> = ({
  helpRequestedProblemData,
  ...previewProps
}) => {
  const response: ProblemFromSWR = {
    mutate: () => {},
    problemData: helpRequestedProblemData!,
    isValidating: false,
  };
  return <ProblemPreview {...previewProps} {...response} />;
};

const SampleProblemFetcher: FC<FullscreenProblemFetcherProps> = ({
  currentType,
  ...previewProps
}) => {
  const response = useExampleProblem({ skillType: currentType! });
  return <ProblemPreview {...previewProps} {...response} />;
};

export const FullscreenProblemFetcher: FC<FullscreenProblemFetcherProps> = ({
  problemType,
  ...props
}) => {
  switch (problemType) {
    case 'lockedExamTemplate':
      return <LockedExamTemplateFetcher problemType={problemType} {...props} />;
    case 'skillDiscoveryLockedExamProblem':
      return (
        <SkillDiscoveryLockedExamProblem problemType={problemType} {...props} />
      );
    case 'current':
      return <CurrentProblemFetcher problemType={problemType} {...props} />;
    case 'exam':
      return <ExamProblemFetcher problemType={problemType} {...props} />;
    case 'helpRequested':
      return <HelpRequestedFetcher problemType={problemType} {...props} />;
    case 'sample':
      return <SampleProblemFetcher problemType={problemType} {...props} />;
    default:
      throw new Error('Unknown problem type');
  }
};
